import {PortableText} from '@portabletext/react';

import getYouTubeId from 'get-youtube-id';

import {TypedObject} from '@sanity/types/dist/dts/index';
import {Text} from './index';
import {Table} from './Table.client';
import YoutubeEmbed from './YoutubeEmbed.client';
import SanityImage from './media/SanityImage.client';
import sanityConfig from '../../sanity.config';
import clsx from 'clsx';

interface CopyProps {
  copy: TypedObject[];
  notProse?: boolean;
}

export function Copy({copy, notProse}: CopyProps) {
  return (
    <div
      className={clsx('max-w-full', {
        prose: !notProse,
      })}
    >
      <PortableText
        onMissingComponent={false}
        value={copy}
        components={{
          marks: {
            color: ({children, markType, value}) => {
              return (
                <span
                  style={{
                    color: markType === 'color' ? value.hex : 'inherit',
                  }}
                  className="not-prose"
                >
                  {children}
                </span>
              );
            },
            link: ({children, value}) => (
              <a href={value.href} className="hover:underline text-blue-500">
                {children}
              </a>
            ),
          },
          block: {
            h1: ({children}) => <Text as="h1">{children}</Text>,
            h2: ({children}) => <Text as="h2">{children}</Text>,
            h3: ({children}) => <Text as="h3">{children}</Text>,
            h4: ({children}) => <Text as="h4">{children}</Text>,
            h5: ({children}) => <Text as="h5">{children}</Text>,
            h6: ({children}) => <Text as="h6">{children}</Text>,
            p: ({children}) => <Text as="p">{children}</Text>,
          },
          types: {
            image: ({value}) => {
              const {asset, alt, width} = value;

              if (!asset || !asset._ref) {
                return null;
              }

              return (
                <div
                  style={{
                    width: width ? `${width}%` : '100%',
                  }}
                >
                  <SanityImage
                    alt={alt}
                    dataset={sanityConfig.dataset}
                    layout="responsive"
                    objectFit="cover"
                    projectId={sanityConfig.projectId}
                    src={asset._ref}
                  />
                </div>
              );
            },
            youtube: ({value}) => {
              const {url} = value;
              const id = getYouTubeId(url);

              if (!id) {
                return null;
              }

              return <YoutubeEmbed title="" id={id} />;
            },
            table: ({value}) => {
              return (
                <Table
                  table={{
                    _id: value._id,
                    table: value,
                    firstRowIsHeader: true,
                    _type: 'tableInternal',
                  }}
                />
              );
            },
            sideBySideImages: ({value}) => {
              return (
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 not-prose">
                  {value?.leftImage?.asset?._ref && (
                    <SanityImage
                      dataset={sanityConfig.dataset}
                      layout="responsive"
                      objectFit="cover"
                      projectId={sanityConfig.projectId}
                      src={value.leftImage.asset._ref}
                    />
                  )}
                  {value?.rightImage?.asset?._ref && (
                    <SanityImage
                      dataset={sanityConfig.dataset}
                      layout="responsive"
                      objectFit="cover"
                      projectId={sanityConfig.projectId}
                      src={value.rightImage.asset._ref}
                    />
                  )}
                </div>
              );
            },
          },
        }}
      />
    </div>
  );
}
